/**
 * Latency Bar Chart Component
 * Horizontal bar chart showing latency breakdown (total, cache, backend)
 */

import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/cn';

export interface LatencyChartProps {
  avgLatency: number;      // Total average latency
  cacheLatency: number;    // Cache lookup latency
  backendLatency: number;  // Backend request latency
  className?: string;
}

interface BarData {
  labelKey: 'total' | 'cacheLatency' | 'backend';
  value: number;
  backgroundClass: string;
}

function formatLatency(value: number): string {
  if (value < 1) return '<1ms';
  return `${Math.round(value)}ms`;
}

export function LatencyChart({
  avgLatency,
  cacheLatency,
  backendLatency,
  className = '',
}: LatencyChartProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.charts;

  const bars: BarData[] = [
    {
      labelKey: 'total',
      value: avgLatency,
      backgroundClass: 'bg-primary',
    },
    {
      labelKey: 'cacheLatency',
      value: cacheLatency,
      backgroundClass: 'bg-success',
    },
    {
      labelKey: 'backend',
      value: backendLatency,
      backgroundClass: 'bg-warning',
    },
  ];

  // Find max value for scaling
  const maxValue = Math.max(...bars.map(b => b.value), 1);

  return (
    <Card className={`${className}`}>
      <h3 className="text-sm font-medium text-muted-foreground mb-4">{t.avgLatency}</h3>
      <div className="space-y-3" style={{ minHeight: 160 }}>
        {bars.map((bar) => {
          const widthPercent = (bar.value / maxValue) * 100;
          const label = t[bar.labelKey];
          return (
            <div key={bar.labelKey} className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground w-16">{label}</span>
              <div className="flex flex-1 items-center gap-2">
                <div className="flex-1 h-8 bg-muted rounded overflow-hidden relative">
                  <div
                    className={cn(
                      'h-full rounded transition-all duration-500 ease-out',
                      bar.backgroundClass,
                    )}
                    style={{
                      width: `${Math.max(widthPercent, 2)}%`,
                    }}
                  />
                </div>
                <span className="w-12 text-right text-xs font-medium text-foreground whitespace-nowrap">
                  {formatLatency(bar.value)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
